import { useEffect, useMemo, useState } from "react";
import { formatUzPhone, initialPhoneInput, isBlankPhoneInput } from "../phone";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Plus, Users, Pencil, Copy, Trash2, X } from "lucide-react";
import { PageFilterPopover } from "../components/page-filter-popover";
import { usePageHeader } from "../lib/use-page-header";
import { api, type BranchOut, type EmployeeRow } from "../api";
import { fmtInt, fmtMoney } from "../lib/format";
import { MiniKpi } from "../components/mini-kpi";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { EmptyState } from "../components/ui/empty-state";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { FeatureGate } from "../components/feature-gate";
import { Money, Mono } from "../components/ui/ledger";
import { useFeature, useTariff } from "../lib/use-tariff";
import { useFinance } from "../lib/use-finance";
import { useFinanceText } from "../lib/finance-text";
import { EmployeeFinance } from "../components/finance-form";

interface Props {
  onOpenTariff: () => void;
}

export function EmployeesPage({ onOpenTariff }: Props) {
  return (
    <FeatureGate feature="employees" requiredTariff="standard" onUpgrade={onOpenTariff}>
      <EmployeesInner onOpenTariff={onOpenTariff} />
    </FeatureGate>
  );
}

function EmployeesInner({ onOpenTariff }: Props) {
  const { t } = useTranslation(["employees", "common"]);
  const [rows, setRows] = useState<EmployeeRow[]>([]);
  const [branches, setBranches] = useState<BranchOut[]>([]);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [nameFilter, setNameFilter] = useState("");
  const [phoneFilter, setPhoneFilter] = useState("");
  const [branchFilter, setBranchFilter] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState<EmployeeRow | null>(null);
  const [deleting, setDeleting] = useState<EmployeeRow | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [createdCreds, setCreatedCreds] = useState<{
    phone: string;
    password: string;
  } | null>(null);
  const [refresh, setRefresh] = useState(0);
  const [loading, setLoading] = useState(true);
  const { status, reload: reloadTariff } = useTariff();
  // Sotuvchi/omborchi huquqlari faqat ombor moduli bor tarifda ma'noga ega —
  // comfort'da POS/kirim ekranlarining o'zi yo'q, shuning uchun bu ikki
  // katakcha "o'lik variant" bo'lib qolardi va butunlay yashiriladi.
  const hasProducts = useFeature("tovars");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.employees(from || undefined, to || undefined);
        setRows(r);
        setErr(null);
      } catch (e) {
        setErr((e as { message?: string }).message || t("error"));
      } finally {
        setLoading(false);
      }
    })();
  }, [from, to, refresh]);

  useEffect(() => {
    // Arxivlangan filiallar ham saqlanadi: xodim arxivlangan filialga
    // biriktirilgan bo'lishi mumkin va kesimda uni nomsiz ko'rsatib
    // bo'lmaydi. Qatordagi tanlovga esa faqat faollari beriladi.
    api
      .branches()
      .then(setBranches)
      .catch(() => {});
  }, []);

  const activeBranches = useMemo(
    () => branches.filter((b) => b.is_active),
    [branches],
  );
  const hasBranches = activeBranches.length > 0;

  /**
   * Bitta qator uchun filial variantlari: faol filiallar + (agar xodim
   * ARXIVLANGAN filialga biriktirilgan bo'lsa) o'sha filialning o'zi.
   *
   * Aks holda `select` qiymati hech bir variantga tushmay, brauzer birinchi
   * variantni ("—") ko'rsatardi: xodim filialsizdek ko'rinib, egasi buni
   * to'g'rilashga urinib haqiqiy biriktirmani o'chirib yuborishi mumkin edi.
   */
  function branchOptionsFor(branchId: number | null): BranchOut[] {
    const current =
      branchId == null ? null : branches.find((b) => b.id === branchId);
    if (current && !current.is_active) return [...activeBranches, current];
    return activeBranches;
  }

  async function saveBranch(id: number, value: string) {
    try {
      await api.updateEmployee(id, { branch_id: value ? Number(value) : null });
      toast.success(t("toast.branchUpdated"));
      setRefresh((x) => x + 1);
    } catch (e) {
      toast.error((e as { message?: string }).message || t("error"));
    }
  }

  const totalSum = rows.reduce((s, r) => s + parseFloat(r.total_sum || "0"), 0);
  const activeCount = rows.filter((r) => r.faol).length;
  const totalWorks = rows.reduce((s, r) => s + r.works_count, 0);
  const filteredRows = useMemo(() => {
    const terms = nameFilter.toLocaleLowerCase().trim().split(/\s+/).filter(Boolean);
    const phone = phoneFilter.trim();
    return rows.filter((row) => {
      const name = row.full_name.toLocaleLowerCase();
      return terms.every((term) => name.includes(term)) &&
        (!phone || row.phone.includes(phone)) &&
        (!branchFilter || String(row.branch_id ?? "none") === branchFilter);
    });
  }, [rows, nameFilter, phoneFilter, branchFilter]);

  /**
   * Filial kesimi. Guruhlash `branch_id` bo'yicha; nom TO'LIQ filial
   * ro'yxatidan olinadi, shuning uchun arxivlangan filialdagi xodim ham
   * nomi bilan chiqadi. Tartib: faol filiallar → arxivlanganlar → "Filialsiz"
   * (oxirgisi faqat biriktirilmagan xodim bo'lsa).
   */
  const branchRows = useMemo(() => {
    const byId = new Map(branches.map((b) => [b.id, b]));
    const groups = new Map<
      number | null,
      { employees: number; active: number; works: number; sum: number }
    >();
    for (const r of rows) {
      const key = r.branch_id ?? null;
      const g = groups.get(key) ?? {
        employees: 0,
        active: 0,
        works: 0,
        sum: 0,
      };
      g.employees += 1;
      if (r.faol) g.active += 1;
      g.works += r.works_count;
      g.sum += parseFloat(r.total_sum || "0");
      groups.set(key, g);
    }
    return [...groups.entries()]
      .map(([branchId, g]) => {
        const branch = branchId == null ? null : byId.get(branchId);
        return {
          branchId,
          // Filial o'chib ketgan bo'lsa (kesh eskirgan) — "Filialsiz" emas,
          // shunchaki nomsiz qator bo'lmasligi uchun zaxira yorliq.
          name: branchId == null ? t("byBranch.noBranch") : (branch?.name ?? "—"),
          archived: branch != null && !branch.is_active,
          ...g,
        };
      })
      .sort((a, b) => {
        // "Filialsiz" doim oxirida, arxivlanganlar faollardan keyin.
        if ((a.branchId == null) !== (b.branchId == null)) {
          return a.branchId == null ? 1 : -1;
        }
        if (a.archived !== b.archived) return a.archived ? 1 : -1;
        return a.name.localeCompare(b.name);
      });
  }, [rows, branches, t]);

  async function saveCap(
    id: number,
    field: "can_be_usta" | "can_be_sotuvchi" | "can_be_omborchi",
    value: boolean,
  ) {
    try {
      await api.updateEmployee(id, { [field]: value });
      setRefresh((x) => x + 1);
    } catch (e) {
      toast.error((e as { message?: string }).message || t("error"));
    }
  }

  async function saveActive(id: number, faol: boolean) {
    try {
      await api.updateEmployee(id, { faol });
      toast.success(faol ? t("toast.activated") : t("toast.deactivated"));
      setRefresh((x) => x + 1);
      // Deaktivatsiya o'rin (seat) bo'shatadi, reaktivatsiya esa egallaydi —
      // limit bannerini va "xodim qo'shish" tugmasini darhol yangilaymiz,
      // aks holda ketgan ustani deaktiv qilgan ega yangisini qo'sha olmaydi.
      reloadTariff();
    } catch (e) {
      toast.error((e as { message?: string }).message || t("error"));
    }
  }

  const limits = status?.limits;
  const limitReached =
    limits?.max_users != null && limits.current_users >= limits.max_users;

  const filterCount = (from ? 1 : 0) + (to ? 1 : 0);
  const clearFilters = () => {
    setFrom("");
    setTo("");
  };

  async function confirmDelete() {
    if (!deleting) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await api.deleteEmployee(deleting.id);
      toast.success(t("toast.deleted"));
      setDeleting(null);
      setRefresh((x) => x + 1);
      reloadTariff();
    } catch (e) {
      setDeleteError((e as { message?: string }).message || t("error"));
    } finally {
      setDeleteBusy(false);
    }
  }

  const filterNode = useMemo(
    () => (
      <PageFilterPopover count={filterCount} onClear={clearFilters}>
        <div className="space-y-1.5">
          <Label htmlFor="from">{t("filters.from")}</Label>
          <Input
            id="from"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="to">{t("filters.to")}</Label>
          <Input
            id="to"
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
        </div>
      </PageFilterPopover>
    ),
    [from, to, filterCount, t],
  );

  const actionsNode = useMemo(
    () => (
      <Button
        size="sm"
        onClick={() => setShowAdd(true)}
        disabled={limitReached}
      >
        <Plus />
        <span className="hidden sm:inline">{t("actions.newEmployee")}</span>
      </Button>
    ),
    [limitReached, t],
  );

  usePageHeader({ filters: filterNode, actions: actionsNode, filterCount });

  return (
    <div className="employees-page space-y-4">
      {limitReached && (
        <div className="rounded-md border border-warning/30 bg-warning/10 text-warning px-4 py-3 text-sm flex items-center justify-between gap-3 flex-wrap">
          <span>
            {t("limit.reached", {
              current: limits?.current_users,
              max: limits?.max_users,
            })}
          </span>
          <Button size="sm" variant="outline" onClick={onOpenTariff}>
            {t("limit.upgrade")}
          </Button>
        </div>
      )}

      {err && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3 text-sm">
          {err}
        </div>
      )}

      {/* Xodimlar soni davrga bog'liq emas. Ish va summa esa sana tanlansa
          shu kesimda, aks holda barcha davrlar bo'yicha ko'rsatiladi. */}
      <div className="space-y-1.5">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {loading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-[92px]" />
            ))
          ) : (
            <>
              <Card className="border-dashed">
                <CardContent className="p-4">
                  <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">{t("overview.total")}</div>
                  <div className="font-num text-2xl font-bold tabular-nums mt-1">{fmtInt(rows.length)}</div>
                  <div className="mt-3 grid grid-cols-2 gap-3 border-t pt-3 text-sm">
                    <div><span className="text-muted-foreground">{t("overview.active")}</span><strong className="ml-2 tabular-nums">{fmtInt(activeCount)}</strong></div>
                    <div><span className="text-muted-foreground">{t("overview.inactive")}</span><strong className="ml-2 tabular-nums">{fmtInt(rows.length - activeCount)}</strong></div>
                  </div>
                </CardContent>
              </Card>
              <MiniKpi label={t("overview.works")} value={fmtInt(totalWorks)} />
              <MiniKpi label={t("overview.revenue")} value={fmtMoney(totalSum)} />
            </>
          )}
        </div>
        {!loading && rows.length > 0 && (
          <p className="text-xs text-muted-foreground">{t("overview.rangeNote")}</p>
        )}
      </div>

      {/* Kesim FILIAL BO'LSA ko'rsatiladi — arxivlangani ham hisobga olinadi
          (aks holda hamma filiali arxivlangan ustaxonada xodimlar kesimsiz
          qolardi). Filialsiz ustaxonada bu jadval bitta "Filialsiz" qatordan
          iborat bo'lib, hech narsa qo'shmasdi. */}
      {!loading && branches.length > 0 && branchRows.length > 0 && (
        <Card className="employees-branch-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t("byBranch.title")}</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("byBranch.columns.branch")}</TableHead>
                  <TableHead className="text-right">
                    {t("byBranch.columns.employees")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("byBranch.columns.active")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("byBranch.columns.works")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("byBranch.columns.revenue")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {branchRows.map((b) => (
                  <TableRow
                    key={b.branchId ?? "none"}
                    className={b.archived ? "opacity-60" : ""}
                  >
                    <TableCell className="font-medium">
                      {b.name}
                      {b.archived && (
                        <span className="ml-1.5 text-xs text-muted-foreground">
                          {t("byBranch.archived")}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {b.employees}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{b.active}</TableCell>
                    <TableCell className="text-right tabular-nums">{b.works}</TableCell>
                    <TableCell className="text-right">
                      <Money>{fmtMoney(b.sum)}</Money>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {!loading && rows.length > 0 && (
        <Card className="employees-filter-card">
          <CardContent className="p-4">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_220px_auto] lg:items-end">
              <div className="space-y-1.5">
                <Label htmlFor="employee-name-filter">{t("listFilters.name")}</Label>
                <Input id="employee-name-filter" value={nameFilter} onChange={(e) => setNameFilter(e.target.value)} placeholder={t("listFilters.namePlaceholder")} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="employee-phone-filter">{t("listFilters.phone")}</Label>
                <Input id="employee-phone-filter" value={phoneFilter} onChange={(e) => setPhoneFilter(e.target.value)} placeholder="+998" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="employee-branch-filter">{t("listFilters.branch")}</Label>
                <select id="employee-branch-filter" className="ui-control flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm" value={branchFilter} onChange={(e) => setBranchFilter(e.target.value)}>
                  <option value="">{t("listFilters.allBranches")}</option>
                  {branches.map((b) => <option key={b.id} value={b.id}>{b.name}{b.is_active ? "" : ` (${t("byBranch.archived")})`}</option>)}
                  <option value="none">{t("byBranch.noBranch")}</option>
                </select>
              </div>
              {(nameFilter || phoneFilter || branchFilter) && (
                <Button variant="ghost" onClick={() => { setNameFilter(""); setPhoneFilter(""); setBranchFilter(""); }}><X />{t("listFilters.clear")}</Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-12" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<Users />}
              title={t("empty.title")}
              description={t("empty.description")}
              action={
                !limitReached && (
                  <Button onClick={() => setShowAdd(true)}>
                    <Plus />
                    {t("actions.newEmployee")}
                  </Button>
                )
              }
            />
          ) : filteredRows.length === 0 ? (
            <EmptyState icon={<Users />} title={t("listFilters.noResults")} description={t("listFilters.noResultsHint")} />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("table.name")}</TableHead>
                  <TableHead>{t("table.phone")}</TableHead>
                  <TableHead className="text-center">{t("table.usta")}</TableHead>
                  {hasProducts && (
                    <>
                      <TableHead className="text-center">{t("table.sotuvchi")}</TableHead>
                      <TableHead className="text-center">{t("table.ombor")}</TableHead>
                    </>
                  )}
                  <TableHead className="text-center">{t("table.status")}</TableHead>
                  {hasBranches && <TableHead>{t("table.branch")}</TableHead>}
                  <TableHead className="text-right">{t("table.actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRows.map((r) => (
                  <TableRow key={r.id} className={r.faol ? "" : "opacity-50"}>
                    <TableCell className="font-medium">{r.full_name}</TableCell>
                    <TableCell>
                      <Mono className="text-muted-foreground">{formatUzPhone(r.phone)}</Mono>
                    </TableCell>
                    <TableCell className="text-center">
                      <input
                        type="checkbox"
                        className="size-4 accent-primary"
                        checked={r.can_be_usta}
                        onChange={(e) => saveCap(r.id, "can_be_usta", e.target.checked)}
                      />
                    </TableCell>
                    {hasProducts && (
                      <>
                        <TableCell className="text-center">
                          <input
                            type="checkbox"
                            className="size-4 accent-primary"
                            checked={r.can_be_sotuvchi}
                            onChange={(e) => saveCap(r.id, "can_be_sotuvchi", e.target.checked)}
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          <input
                            type="checkbox"
                            className="size-4 accent-primary"
                            checked={r.can_be_omborchi}
                            onChange={(e) => saveCap(r.id, "can_be_omborchi", e.target.checked)}
                          />
                        </TableCell>
                      </>
                    )}
                    <TableCell className="text-center">
                      <Button
                        size="sm"
                        variant={r.faol ? "outline" : "default"}
                        className="h-7"
                        onClick={() => saveActive(r.id, !r.faol)}
                      >
                        {r.faol ? t("row.deactivate") : t("row.activate")}
                      </Button>
                    </TableCell>
                    {hasBranches && <TableCell>
                        <select
                          className="ui-control h-8 rounded-md border border-input bg-background px-2 text-sm"
                          value={r.branch_id ?? ""}
                          onChange={(e) => saveBranch(r.id, e.target.value)}
                        >
                          <option value="">{t("row.branchNone")}</option>
                          {branchOptionsFor(r.branch_id).map((b) => (
                            <option key={b.id} value={b.id}>
                              {b.is_active
                                ? b.name
                                : `${b.name} (${t("byBranch.archived")})`}
                            </option>
                          ))}
                        </select>
                    </TableCell>}
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        aria-label={t("row.edit")}
                        onClick={() => setEditing(r)}
                      >
                        <Pencil />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        aria-label={t("row.delete")}
                        onClick={() => { setDeleteError(null); setDeleting(r); }}
                      >
                        <Trash2 />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <AddEmployeeForm
          branches={activeBranches}
          onCancel={() => setShowAdd(false)}
          onCreated={(creds) => {
            setShowAdd(false);
            setRefresh((x) => x + 1);
            reloadTariff();
            setCreatedCreds(creds);
          }}
        />
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        {editing && (
          <EditEmployeeForm
            employee={editing}
            onCancel={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              setRefresh((x) => x + 1);
            }}
          />
        )}
      </Dialog>

      <Dialog open={!!createdCreds} onOpenChange={(o) => !o && setCreatedCreds(null)}>
        {createdCreds && (
          <CredentialsCard
            creds={createdCreds}
            onClose={() => setCreatedCreds(null)}
          />
        )}
      </Dialog>

      <Dialog open={!!deleting} onOpenChange={(open) => !open && !deleteBusy && setDeleting(null)}>
        {deleting && (
          <div>
            <DialogHeader><DialogTitle>{t("delete.title")}</DialogTitle><DialogDescription>{t("delete.confirm")}</DialogDescription></DialogHeader>
            <DialogBody>{deleteError && <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">{deleteError}</div>}</DialogBody>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleting(null)} disabled={deleteBusy}>{t("common:actions.cancel")}</Button>
              <Button variant="destructive" onClick={confirmDelete} disabled={deleteBusy}>{deleteBusy ? t("delete.deleting") : t("delete.action")}</Button>
            </DialogFooter>
          </div>
        )}
      </Dialog>
    </div>
  );
}

function AddEmployeeForm({
  branches,
  onCancel,
  onCreated,
}: {
  branches: BranchOut[];
  onCancel: () => void;
  onCreated: (creds: { phone: string; password: string }) => void;
}) {
  const { t } = useTranslation(["employees", "common"]);
  const hasProducts = useFeature("tovars");
  const finance = useFinance();
  const ft = useFinanceText();
  const [employmentStart, setEmploymentStart] = useState("");
  const [employmentEnd, setEmploymentEnd] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("+998");
  const [password, setPassword] = useState("");
  const [branchId, setBranchId] = useState<string>("");
  const [canUsta, setCanUsta] = useState(true);
  const [canSotuvchi, setCanSotuvchi] = useState(false);
  const [canOmborchi, setCanOmborchi] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (isBlankPhoneInput(phone)) { setErr(t("error")); return; }
    setBusy(true);
    setErr(null);
    try {
      const created = (await api.createEmployee({
        full_name: name,
        ...(finance.active("ish_haqi") ? { employment_start: employmentStart || null, employment_end: employmentEnd || null } : {}),
        phone,
        password,
        branch_id: branchId ? Number(branchId) : null,
        can_be_usta: canUsta,
        // Katakcha yashirilgan tarifda hech qachon `true` yuborilmasin.
        can_be_sotuvchi: hasProducts && canSotuvchi,
        can_be_omborchi: hasProducts && canOmborchi,
      })) as EmployeeRow;
      toast.success(t("toast.employeeAdded"));
      // Telefonni server normallashtirilgan holda qaytaradi — mobil ilovaga
      // kiritiladigan qiymat bilan bir xil bo'lishi uchun o'shani ko'rsatamiz.
      onCreated({ phone: created.phone, password });
    } catch (e) {
      setErr((e as { message?: string }).message || t("error"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit}>
      <DialogHeader>
        <DialogTitle>{t("add.title")}</DialogTitle>
        <DialogDescription>
          {t("add.description")}
        </DialogDescription>
      </DialogHeader>
      <DialogBody className="space-y-4">
        {finance.active("ish_haqi") && <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-sm">{ft("employment_start")} ({ft("optional")})<Input type="date" value={employmentStart} onChange={e => setEmploymentStart(e.target.value)} /></label>
          <label className="text-sm">{ft("employment_end")} ({ft("optional")})<Input type="date" value={employmentEnd} onChange={e => setEmploymentEnd(e.target.value)} /></label>
        </div>}
        <div className="space-y-1.5">
          <Label htmlFor="name">{t("add.nameLabel")}</Label>
          <Input
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("add.namePlaceholder")}
            required
            autoFocus
            disabled={busy}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="phone">{t("add.phoneLabel")}</Label>
          <Input
            id="phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+998901234567"
            required
            disabled={busy}
          />
          <p className="text-xs text-muted-foreground">{t("phoneLoginHint")}</p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password">{t("add.passwordLabel")}</Label>
          <Input
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            type="text"
            placeholder={t("add.passwordPlaceholder")}
            minLength={4}
            required
            disabled={busy}
          />
          <p className="text-xs text-muted-foreground">
            {t("add.passwordHint")}
          </p>
        </div>
        {branches.length > 0 && (
          <div className="space-y-1.5">
            <Label htmlFor="emp-branch">{t("add.branchLabel")}</Label>
            <select
              id="emp-branch"
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={branchId}
              onChange={(e) => setBranchId(e.target.value)}
              disabled={busy}
            >
              <option value="">{t("add.branchNone")}</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="space-y-2">
          <Label>{t("caps.title")}</Label>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" className="size-4 accent-primary" checked={canUsta} onChange={(e) => setCanUsta(e.target.checked)} disabled={busy} />
              <span>{t("caps.usta")}</span>
            </label>
            {hasProducts && (
              <>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" className="size-4 accent-primary" checked={canSotuvchi} onChange={(e) => setCanSotuvchi(e.target.checked)} disabled={busy} />
                  <span>{t("caps.sotuvchi")}</span>
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" className="size-4 accent-primary" checked={canOmborchi} onChange={(e) => setCanOmborchi(e.target.checked)} disabled={busy} />
                  <span>{t("caps.omborchi")}</span>
                </label>
              </>
            )}
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          {t("add.footerHint")}
        </p>
        {err && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 text-destructive px-3 py-2 text-sm">
            {err}
          </div>
        )}
      </DialogBody>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          {t("common:actions.cancel")}
        </Button>
        <Button type="submit" disabled={busy}>
          {busy ? t("form.saving") : t("common:actions.save")}
        </Button>
      </DialogFooter>
    </form>
  );
}

function EditEmployeeForm({
  employee,
  onCancel,
  onSaved,
}: {
  employee: EmployeeRow;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation(["employees", "common"]);
  const hasProducts = useFeature("tovars");
  const [name, setName] = useState(employee.full_name);
  const [phone, setPhone] = useState(initialPhoneInput(employee.phone));
  const [password, setPassword] = useState("");
  const [canUsta, setCanUsta] = useState(employee.can_be_usta);
  const [canSotuvchi, setCanSotuvchi] = useState(employee.can_be_sotuvchi);
  const [canOmborchi, setCanOmborchi] = useState(employee.can_be_omborchi);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (isBlankPhoneInput(phone)) { setErr(t("error")); return; }
    setBusy(true);
    setErr(null);
    try {
      const payload: Record<string, unknown> = {
        full_name: name,
        phone,
        can_be_usta: canUsta,
      };
      // Katakchalar yashirilgan tarifda bu maydonlar umuman tegilmaydi —
      // tarif ko'tarilsa avvalgi qiymatlar joyida qoladi.
      if (hasProducts) {
        payload.can_be_sotuvchi = canSotuvchi;
        payload.can_be_omborchi = canOmborchi;
      }
      // Parol faqat to'ldirilganda yuboriladi — bo'sh bo'lsa mavjud parol saqlanadi.
      if (password.trim()) payload.password = password;
      await api.updateEmployee(employee.id, payload);
      toast.success(t("toast.saved"));
      onSaved();
    } catch (e) {
      setErr((e as { message?: string }).message || t("error"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit}>
      <DialogHeader>
        <DialogTitle>{t("edit.title")}</DialogTitle>
        <DialogDescription>{employee.full_name}</DialogDescription>
      </DialogHeader>
      <DialogBody className="space-y-4">
        <EmployeeFinance employee={employee} />
        <div className="space-y-1.5">
          <Label htmlFor="edit-name">{t("edit.nameLabel")}</Label>
          <Input
            id="edit-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
            disabled={busy}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="edit-phone">{t("edit.phoneLabel")}</Label>
          <Input
            id="edit-phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+998901234567"
            required
            disabled={busy}
          />
          <p className="text-xs text-muted-foreground">{t("phoneLoginHint")}</p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="edit-password">{t("edit.passwordLabel")}</Label>
          <Input
            id="edit-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            type="text"
            placeholder={t("edit.passwordPlaceholder")}
            disabled={busy}
          />
          <p className="text-xs text-muted-foreground">
            {t("edit.passwordHint")}
          </p>
        </div>
        <div className="space-y-2">
          <Label>{t("caps.title")}</Label>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" className="size-4 accent-primary" checked={canUsta} onChange={(e) => setCanUsta(e.target.checked)} disabled={busy} />
              <span>{t("caps.usta")}</span>
            </label>
            {hasProducts && (
              <>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" className="size-4 accent-primary" checked={canSotuvchi} onChange={(e) => setCanSotuvchi(e.target.checked)} disabled={busy} />
                  <span>{t("caps.sotuvchi")}</span>
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" className="size-4 accent-primary" checked={canOmborchi} onChange={(e) => setCanOmborchi(e.target.checked)} disabled={busy} />
                  <span>{t("caps.omborchi")}</span>
                </label>
              </>
            )}
          </div>
        </div>
        {err && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 text-destructive px-3 py-2 text-sm">
            {err}
          </div>
        )}
      </DialogBody>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          {t("common:actions.cancel")}
        </Button>
        <Button type="submit" disabled={busy}>
          {busy ? t("form.saving") : t("common:actions.save")}
        </Button>
      </DialogFooter>
    </form>
  );
}

function CredentialsCard({
  creds,
  onClose,
}: {
  creds: { phone: string; password: string };
  onClose: () => void;
}) {
  const { t } = useTranslation(["employees", "common"]);
  function copy() {
    navigator.clipboard
      .writeText(`${t("creds.phone")}${creds.phone}\n${t("creds.password")}${creds.password}`)
      .then(
        () => toast.success(t("toast.copied")),
        () => toast.error(t("toast.copyError")),
      );
  }

  return (
    <div>
      <DialogHeader>
        <DialogTitle>{t("creds.title")}</DialogTitle>
        <DialogDescription>
          {t("creds.description")}
        </DialogDescription>
      </DialogHeader>
      <DialogBody className="space-y-3">
        <div className="rounded-md bg-muted/60 p-3 font-mono text-sm space-y-1">
          <div>
            <span className="text-muted-foreground">{t("creds.phone")}</span>
            {formatUzPhone(creds.phone)}
          </div>
          <div>
            <span className="text-muted-foreground">{t("creds.password")}</span>
            {creds.password}
          </div>
        </div>
        <Button type="button" variant="outline" className="w-full" onClick={copy}>
          <Copy />
          {t("creds.copy")}
        </Button>
      </DialogBody>
      <DialogFooter>
        <Button type="button" onClick={onClose}>
          {t("common:actions.close")}
        </Button>
      </DialogFooter>
    </div>
  );
}
